import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./MobileOrderDashboard.css";
import MobileHeader from "./MobileHeader";
import MobileOrderTypeSelector from "./MobileOrderTypeSelector";
import MobileCategories from "./MobileCategories";
import MobileItemsList from "./MobileItemsList";
import MobileTableView from "./MobileTableView";
import MobileBottomNavigation from "./MobileBottomNavigation";
import MobileCart from "./MobileCart";
import MobileOrderSummaryBanner from "../components/MobileOrderSummaryBanner";
import { TYPESOFORDERS } from "../../../contants";
import { logoutUser } from "../../../redux/action/userActions";
import { findActiveOrderIndex } from "../../../redux/reducers/newOrderReducer";
import { 
  getTableTypeFromOrder, 
  getRoundOffType,
  getCompleteOrderTotals 
} from "../helpers/orderHelpers";
import calculateOrderTotals from "../../../helpers/calculateOrderTotals";
import roundOffTotal from "../../../helpers/roundOffTotal";

const MobileOrderDashboard = ({
  handleKot,
  handleCustomerForm,
  handleItemClick,
  handleTableNumberClick,
  handleOtherOrderClick,
}) => {
  const [currentView, setCurrentView] = useState("home"); // home, tables, orders, cart
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const dispatch = useDispatch();
  
  const { cgst, sgst } = useSelector((state) => state.user);
  const {
    activeOrders,
    allItems,
    allTables,
    itemCategories,
    selectedOrderTypeId,
    activeOrder,
  } = useSelector((state) => state.order);
  const { tables } = useSelector((state) => state.branch);
  const { 
    exclusiveGST, 
    lowerRoundOff, 
    upperRoundOff 
  } = useSelector((state) => state.util);

  const orderIndex = findActiveOrderIndex(activeOrders, activeOrder);
  const activeOrderData = activeOrders[orderIndex];

  // Get table type using shared helper
  const tableKind = getTableTypeFromOrder(tables, activeOrderData);

  // Get round-off type using shared helper
  const roundOffType = getRoundOffType(lowerRoundOff, upperRoundOff);

  // Calculate order totals using shared helper
  const orderTotals = getCompleteOrderTotals(
    activeOrderData,
    cgst,
    sgst,
    tableKind,
    exclusiveGST,
    roundOffType,
    calculateOrderTotals,
    roundOffTotal
  );

  const { roundedGrandTotal: grandTotal } = orderTotals;

  const getFilteredItems = () => {
    let items = allItems;

    // Filter by search
    if (searchQuery) {
      items = items.filter((item) =>
        item.itemName?.toLowerCase().includes(searchQuery?.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== "all") {
      items = items.filter((item) => item.categoryName === selectedCategory);
    }

    return items;
  };

  const getTotalCartItems = () => {
    if (
      !activeOrderData ||
      !activeOrderData.orderItems ||
      !Array.isArray(activeOrderData.orderItems)
    )
      return 0;
    return activeOrderData.orderItems.reduce(
      (total, item) => total + (item.quantity || 0),
      0
    );
  };

  const handleTableSelect = (table) => {
    handleTableNumberClick(table);
    // Switch to home view after selecting table to add items
    setTimeout(() => setCurrentView("home"), 300);
  };

  const handleOrderSelect = (order) => {
    handleOtherOrderClick(order);
    // Switch to home view after selecting order
    setTimeout(() => setCurrentView("home"), 300);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
  };

  const renderContent = () => {
    switch (currentView) {
      case "tables":
        return (
          <MobileTableView
            tables={allTables}
            activeOrders={activeOrders}
            handleTableNumberClick={handleTableSelect}
            handleOtherOrderClick={handleOrderSelect}
            selectedOrderTypeId={selectedOrderTypeId}
          />
        );

      case "orders":
        return (
          <MobileTableView
            tables={allTables}
            activeOrders={activeOrders}
            handleTableNumberClick={handleTableSelect}
            handleOtherOrderClick={handleOrderSelect}
            selectedOrderTypeId={selectedOrderTypeId}
            showAllOrders={true}
          />
        );

      case "home":
      default:
        return (
          <>
            <MobileCategories
              categories={itemCategories}
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
            />
            <MobileItemsList
              items={getFilteredItems()}
              onItemClick={handleItemClick}
              activeOrder={activeOrderData}
            />
          </>
        );
    }
  };

  return (
    <div className="d-flex flex-column vh-100 bg-light overflow-hidden p-0">
      {/* Fixed Header */}
      <div className="position-sticky top-0 mobile-fixed-header">
        <MobileHeader
          onSearch={setSearchQuery}
          searchQuery={searchQuery}
          onLogout={handleLogout}
        />

        <MobileOrderTypeSelector
          selectedType={selectedOrderTypeId}
          onSelectType={(typeId) => {
            // When switching to Parcel or Home Delivery, show the orders view
            // When switching to Dine In, show the tables view
            if (typeId === TYPESOFORDERS[0].value) {
              setCurrentView("tables");
            } else {
              setCurrentView("home");
            }
          }}
        />
      </div>

      {/* Active Order Banner - Using Shared Component */}
      <MobileOrderSummaryBanner 
        activeOrder={activeOrderData} 
        grandTotal={grandTotal} 
      />

      {/* Scrollable Content */}
      <div className="flex-fill overflow-auto mobile-scroll-touch">
        {renderContent()}
      </div>

      {/* Bottom Navigation */}
      <MobileBottomNavigation
        currentView={currentView}
        onViewChange={setCurrentView}
        cartItemCount={getTotalCartItems()}
        onCartClick={() => setIsCartOpen(true)}
      />

      {/* Cart Drawer */}
      <MobileCart
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        activeOrder={activeOrderData}
        handleKot={handleKot}
        handleCustomerForm={handleCustomerForm}
      />
    </div>
  );
};

export default MobileOrderDashboard;